import * as React from 'react';

interface UseMobileTableScrollApiOptions {
  /** Total number of data columns (including the pinned first column). */
  columnCount: number;
  /**
   * When true, scrolling wraps around — next from the last column goes back to
   * the first, and prev from the first goes to the last. Prev/next buttons are
   * always enabled.
   */
  infiniteScroll?: boolean;
}

interface UseMobileTableScrollApiResult {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  headerScrollContainerRef: React.RefObject<HTMLDivElement>;
  imageScrollContainerRef: React.RefObject<HTMLDivElement>;
  /** Width (px) for each scrollable column (columns 1+). */
  colStyle: React.CSSProperties | undefined;
  /** Min-width for the full table so it never squishes. */
  tableMinWidth: number | undefined;
  /** Width (px) of the pinned first column. */
  pinnedColWidth: number | undefined;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  currentPage: number;
  totalPages: number;
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollToPage: (index: number) => void;
}

export function useMobileTableScrollApi({
  columnCount,
  infiniteScroll = false,
}: UseMobileTableScrollApiOptions): UseMobileTableScrollApiResult {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const headerScrollContainerRef = React.useRef<HTMLDivElement>(null);
  const imageScrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(0);

  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // When 3+ columns: pin col0 at 50% width, each scrollable col also 50%.
  // When 1–2 columns: all columns share equal width, no pinning needed.
  const pinnedColWidth =
    columnCount > 2 && containerWidth
      ? Math.floor(containerWidth / 2)
      : undefined;

  const scrollableColCount = columnCount > 2 ? columnCount - 1 : columnCount;
  const scrollableColWidth =
    columnCount > 2 && containerWidth
      ? Math.floor(containerWidth / 2)
      : containerWidth
        ? Math.floor(containerWidth / scrollableColCount)
        : undefined;

  const colPxRef = React.useRef<number | undefined>(undefined);
  colPxRef.current = scrollableColWidth;

  const colStyle: React.CSSProperties | undefined = scrollableColWidth
    ? {
        width: scrollableColWidth,
        minWidth: scrollableColWidth,
        maxWidth: scrollableColWidth,
      }
    : undefined;

  const tableMinWidth =
    pinnedColWidth && scrollableColWidth
      ? pinnedColWidth + scrollableColWidth * (columnCount - 1)
      : scrollableColWidth
        ? scrollableColWidth * columnCount
        : undefined;

  const totalPages = columnCount > 2 ? columnCount - 1 : 1;

  const updateScrollState = React.useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (headerScrollContainerRef.current) {
      headerScrollContainerRef.current.scrollLeft = el.scrollLeft;
    }
    if (imageScrollContainerRef.current) {
      imageScrollContainerRef.current.scrollLeft = el.scrollLeft;
    }
    const atStart = el.scrollLeft <= 0;
    const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
    setCanScrollPrev(infiniteScroll || !atStart);
    setCanScrollNext(infiniteScroll || !atEnd);
    const px = colPxRef.current;
    if (px) {
      setCurrentPage(Math.round(el.scrollLeft / px));
    }
  }, [infiniteScroll]);

  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    updateScrollState();
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  // When the user scrolls the sticky header or image row directly, sync back
  // to the main body container so everything moves together.
  // A flag prevents the reverse-sync from firing when the body is already
  // driving the scroll (e.g. carousel buttons), which would cause a loop.
  React.useEffect(() => {
    const header = headerScrollContainerRef.current;
    const image = imageScrollContainerRef.current;
    const body = scrollContainerRef.current;
    if (!header || !body) return;

    let bodyDriving = false;

    const onHeaderScroll = () => {
      if (bodyDriving) return;
      body.scrollLeft = header.scrollLeft;
      if (image) image.scrollLeft = header.scrollLeft;
      updateScrollState();
    };
    const onImageScroll = () => {
      if (bodyDriving || !image) return;
      body.scrollLeft = image.scrollLeft;
      header.scrollLeft = image.scrollLeft;
      updateScrollState();
    };
    const onBodyScroll = () => {
      bodyDriving = true;
      requestAnimationFrame(() => {
        bodyDriving = false;
      });
    };

    header.addEventListener('scroll', onHeaderScroll, { passive: true });
    image?.addEventListener('scroll', onImageScroll, { passive: true });
    body.addEventListener('scroll', onBodyScroll, { passive: true });
    return () => {
      header.removeEventListener('scroll', onHeaderScroll);
      image?.removeEventListener('scroll', onImageScroll);
      body.removeEventListener('scroll', onBodyScroll);
    };
  }, [updateScrollState]);

  React.useEffect(() => {
    updateScrollState();
  }, [containerWidth, updateScrollState]);

  const scrollPrev = React.useCallback(() => {
    const px = colPxRef.current;
    const el = scrollContainerRef.current;
    if (!el || !px) return;
    if (infiniteScroll && el.scrollLeft <= 0) {
      // Wrap to last page
      const lastPage = totalPages - 1;
      el.scrollTo({ left: lastPage * px, behavior: 'smooth' });
    } else {
      el.scrollBy({ left: -px, behavior: 'smooth' });
    }
  }, [infiniteScroll, totalPages]);

  const scrollNext = React.useCallback(() => {
    const px = colPxRef.current;
    const el = scrollContainerRef.current;
    if (!el || !px) return;
    if (
      infiniteScroll &&
      el.scrollLeft >= el.scrollWidth - el.clientWidth - 1
    ) {
      // Wrap to first page
      el.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      el.scrollBy({ left: px, behavior: 'smooth' });
    }
  }, [infiniteScroll]);

  const scrollToPage = React.useCallback((index: number) => {
    const px = colPxRef.current;
    if (!scrollContainerRef.current || !px) return;
    scrollContainerRef.current.scrollTo({
      left: index * px,
      behavior: 'smooth',
    });
  }, []);

  return {
    scrollContainerRef,
    headerScrollContainerRef,
    imageScrollContainerRef,
    colStyle,
    tableMinWidth,
    pinnedColWidth,
    canScrollPrev,
    canScrollNext,
    currentPage,
    totalPages,
    scrollPrev,
    scrollNext,
    scrollToPage,
  };
}
